import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useAuth } from "../../auth/AuthContext";
import { useSearchQuotes, type SearchResult } from "../../queries/useSearchQuotes";
import { GhostMark } from "../GhostMark";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);

  return debounced;
}

function formatUsd(value?: number) {
  if (value === undefined) return "";
  return `$${Math.round(value).toLocaleString()}`;
}

export function SearchBar() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const { data, isFetching, isError } = useSearchQuotes(debouncedQuery, debouncedQuery.length >= 2);

  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const results = (user?.role === "sales"
    ? data?.results.filter((result) => result.quote_id)
    : data?.results
  )?.slice(0, 10) ?? [];

  function selectResult(result: SearchResult) {
    setOpen(false);
    if (result.quote_id) {
      navigate({ to: "/quotes/$quoteId", params: { quoteId: result.quote_id } });
      return;
    }
    navigate({ to: "/customers/$contactId", params: { contactId: String(result.contact_id) } });
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl">
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border border-[#1587c8] after:absolute after:-bottom-1 after:-right-1 after:h-1.5 after:w-px after:rotate-[-45deg] after:bg-[#1587c8]"
      />
      <Input
        className="h-10 border-blue-100 bg-white pl-9 shadow-sm focus:border-[#1587c8]"
        placeholder="Search · Quote ID, name, phone, email"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      />

      {open ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border border-blue-100 bg-white shadow-lg">
          {query.trim().length < 2 ? (
            <div className="p-3 text-xs text-slate-500">
              Type Quote ID, name, phone, or email. Minimum 2 characters.
            </div>
          ) : isError ? (
            <div className="p-3 text-xs text-red-600">Search unavailable. Check API token and endpoint.</div>
          ) : isFetching ? (
            <div className="flex items-center gap-2 p-3 text-xs text-slate-500">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
              Searching ranked results...
            </div>
          ) : results.length > 0 ? (
            <div className="max-h-[420px] overflow-y-auto py-1">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.contact_id}-${result.quote_id || "customer"}`}
                  type="button"
                  className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
                  onClick={() => selectResult(result)}
                >
                  <Badge variant={result.type === "quote" ? "default" : "slate"}>
                    {result.type === "quote" ? "Quote" : "Customer"}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="truncate text-sm font-medium text-slate-950">{result.customer_name}<GhostMark ghosted={result.ghosted} /></div>
                      <div className="shrink-0 text-xs text-slate-500">{result.match_field}</div>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">
                      {result.quote_number ? `#${result.quote_number} · ` : ""}
                      {result.amount ? `${formatUsd(result.amount)} · ` : ""}
                      {result.email}
                    </div>
                  </div>
                </button>
              ))}
              {data && data.total_matches > results.length ? (
                <div className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
                  Showing 10 of {data.total_matches} matches.
                </div>
              ) : null}
            </div>
          ) : (
            <div className="p-3 text-xs text-slate-500">
              {user?.role === "sales" ? "No matching quote found." : "No matching quote or customer found."}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
